import { useState } from "react"
import { useTranslation } from "react-i18next";

/**
 * Hook personalizado para gestionar el estado de errores de inputs en formularios.
 *
 * Estructura del retorno:
 * [0] errorInputs, [1] handleErrorInput, [2] resetErrors, 
 * [3] validForm: Función para validar múltiples campos según reglas básicas.
 *
 * Uso recomendado:
 * const [errorInputs, handleErrorInput, resetErrors, validForm] = useErrorInputs();
 */
export const useErrorInputs = (initialState = {}) => {
    const { t } = useTranslation();
    const [errorInputs, setErrorInputs] = useState(initialState);

    const resetErrors = () => {
        setErrorInputs(initialState);
    }

    const getNestedValue = (obj, path) => {
        if (!path) return undefined;
        return path.split('.').reduce((acc, part) => {
            if (!acc) return undefined;

            if (part.includes('[')) {
                const [key, index] = part.split(/\[|\]/).filter(Boolean);
                return acc[key]?.[Number(index)];
            }

            return acc[part];
        }, obj);
    };

    const handleErrorInput = (input, { isError = false, messageError = '' }) => {
        setErrorInputs((prevErrorInputs) => ({
            ...prevErrorInputs,
            [input]: {
                isError,
                messageError
            }
        }));
    }

    const validForm = (fields, data, validation = null) => {
        let valid = true;
        fields.forEach((item) => {
            const isValid = validField(item, data, validation);
            if (!isValid)
                valid = false;
        });
        return valid;
    };

    const validField = (name, data, validation = null) => {
        const value = getNestedValue(data, name);

        if (value === '' || value === undefined ||
            (typeof value === 'string' && value.trim() === '') ||
            (Array.isArray(value) && value.length === 0)
        ) {
            handleErrorInput(name, { isError: true, messageError: t("validation.requiredField") });
            return false;
        } else {
            handleErrorInput(name, { isError: false, messageError: '' });
            return true;
        }
    };

    return [errorInputs, handleErrorInput, resetErrors, validForm];
}